import { MatToolbarModule } from '@angular/material/toolbar';
import { NgClass } from '@angular/common';
import { Component, ElementRef, OnInit, Renderer2, DOCUMENT, inject } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { ConfigService } from '@config';
import {
  AuthorizedApiService,
  AuthService,
  CanAccessDirective,
  CenterBrandingService,
  InConfiguration,
  LanguageService,
  RightSidebarService,
} from '@core';
import { AuthorizationRequirement, CenterAccess } from '@core/models/interface';
import { MODULE_RESOURCES, moduleAccess, moduleAccessAny, userSelfAccess } from '@core/config/module-access';
import { UnsubscribeOnDestroyAdapter } from '@shared';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
// import { NotificationListComponent } from '../components/notification-list/notification-list.component';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LanguageListComponent } from '../components/language-list/language-list.component';
import { UserProfileMenuComponent } from '../components/user-profile-menu/user-profile-menu.component';
import { LocalStorageService } from '@shared/services/storage.service';
import { RedirectService } from '@core/service/redirectService.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AppConfigService } from 'app/AppConfigService.service';
import { firstValueFrom } from 'rxjs';
import {
  NotificationListComponent,
  Notifications,
} from '../components/notification-list/notification-list.component';
import { SystemUpdateConfig, SystemUpdateListResponse } from '@config/system-updates/system-update.interface';

interface WebPunchAvailabilityBootstrap {
  employee?: {
    employeeId?: string;
    isActive?: boolean;
  } | null;
  workConfig?: {
    punchPolicy?: {
      allowedSources?: string[];
      sourceRules?: Record<string, unknown>;
    };
    sourceRules?: Record<string, unknown>;
  };
}

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  imports: [
    RouterLink,
    NgClass,
    MatButtonModule,
    MatIconModule,
    MatToolbarModule,
    NotificationListComponent,
    MatMenuModule,
    LanguageListComponent,
    UserProfileMenuComponent,
    CanAccessDirective,
    TranslateModule,
  ],
})
export class HeaderComponent
  extends UnsubscribeOnDestroyAdapter
  implements OnInit {
  private document = inject<Document>(DOCUMENT);
  private renderer = inject(Renderer2);
  elementRef = inject(ElementRef);
  private rightSidebarService = inject(RightSidebarService);
  private configService = inject(ConfigService);
  private authService = inject(AuthService);
  private router = inject(Router);
  languageService = inject(LanguageService);
  private localStorageService = inject(LocalStorageService);
  private centerBrandingService = inject(CenterBrandingService);
  private redirectService = inject(RedirectService);
  private translate = inject(TranslateService);
  private appConfigService = inject(AppConfigService);
  private apiService = inject(AuthorizedApiService);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);

  public config!: InConfiguration;
  userImg?: string;
  userName?: string;
  userInitials = 'US';
  homePage?: string;
  isChangingCenter = false;
  userCentersCount = 0;
  availableCenters: CenterAccess[] = [];
  currentCenter = '';
  companyLogo = 'assets/images/logo.png';
  companyName = 'Tdsystem';
  isNavbarCollapsed = true;
  isMobileSettingsOpen = false;
  isMobileLanguageOpen = false;
  isMobileNotificationsOpen = false;
  isMobileSystemUpdatesOpen = false;
  isMobileSettingsGroupOpen = true;
  isMobileCentersGroupOpen = false;
  flagvalue?: string;
  countryName = '';
  langStoreValue?: string;
  defaultFlag?: string;
  isOpenSidebar?: boolean;
  docElement?: HTMLElement;
  isFullScreen = false;
  webPunchEnabled = false;
  readonly userProfileAccess: AuthorizationRequirement = {
    anyOf: [
      ...(userSelfAccess().anyOf || []),
      ...(moduleAccessAny([
        MODULE_RESOURCES.header.userProfile.inbox,
        MODULE_RESOURCES.header.userProfile.settings.modules,
        MODULE_RESOURCES.header.userProfile.settings.centers,
        MODULE_RESOURCES.header.userProfile.settings.roles,
        MODULE_RESOURCES.header.userProfile.settings.permissions,
        MODULE_RESOURCES.header.userProfile.settings.notifications,
        MODULE_RESOURCES.header.userProfile.settings.users,
        MODULE_RESOURCES.header.userProfile.settings.systemUpdates,
      ]).anyOf || []),
    ],
  };
  readonly accountAccess = userSelfAccess();
  readonly webPunchAccess: AuthorizationRequirement = {
    anyOf: [
      { resource: 'attendance-events', action: 'create-self' },
      { resource: MODULE_RESOURCES.attendance.event, action: 'create-self' },
    ],
  };
  readonly inboxAccess = moduleAccess(
    MODULE_RESOURCES.header.userProfile.inbox
  );
  readonly modulosSettingsAccess = moduleAccess(
    MODULE_RESOURCES.header.userProfile.settings.modules
  );
  readonly centersSettingsAccess = moduleAccess(
    MODULE_RESOURCES.header.userProfile.settings.centers
  );
  readonly rolesSettingsAccess = moduleAccess(
    MODULE_RESOURCES.header.userProfile.settings.roles
  );
  readonly createPermissAccess = moduleAccess(
    MODULE_RESOURCES.header.userProfile.settings.permissions
  );
  readonly userAccess = moduleAccess(
    MODULE_RESOURCES.header.userProfile.settings.users
  );
  readonly systemUpdatesAccess = moduleAccess(
    MODULE_RESOURCES.header.userProfile.settings.systemUpdates
  );
  readonly settingsAccess: AuthorizationRequirement = moduleAccessAny([
    MODULE_RESOURCES.header.userProfile.settings.centers,
    MODULE_RESOURCES.header.userProfile.settings.permissions,
    MODULE_RESOURCES.header.userProfile.settings.roles,
    MODULE_RESOURCES.header.userProfile.settings.modules,
    MODULE_RESOURCES.header.userProfile.settings.notifications,
    MODULE_RESOURCES.header.userProfile.settings.users,
    MODULE_RESOURCES.header.userProfile.settings.systemUpdates,
  ]);
  listLang = this.languageService.getImage(this.languageService.getAvailableLangs());
  userNotifications: Notifications[] = [];
  systemUpdateNotifications: Notifications[] = [];
  private readonly seenSystemUpdatesStorageKey = 'seen_system_updates';
  ngOnInit() {
    this.config = this.configService.configData;
    const branding = this.centerBrandingService.getBranding();
    this.companyLogo = branding.companyLogo;
    this.companyName = branding.companyName;
    this.subs.sink = this.centerBrandingService.branding$.subscribe((nextBranding) => {
      this.companyLogo = nextBranding.companyLogo;
      this.companyName = nextBranding.companyName;
    });
    this.syncCurrentUser();
    this.subs.sink = this.authService.change().subscribe(() => this.syncCurrentUser());
    this.docElement = document.documentElement;
    this.homePage = this.redirectService.getDefaultHomeUrl(this.authService.getProfile());
    this.defaultFlag = 'assets/images/flags/us.svg';
    this.subs.sink = this.languageService.availableLangs$.subscribe((langs) => {
      this.listLang = this.languageService.getImage(langs);
      this.syncLanguageSelection(this.languageService.getCurrentLang());
    });
    this.subs.sink = this.languageService.currentLang$.subscribe((lang) => {
      this.syncLanguageSelection(lang);
      this.buildUserNotifications();
      void this.loadSystemUpdates();
    });
    this.buildUserNotifications();
    void this.loadSystemUpdates();
    this.syncLanguageSelection(this.languageService.getCurrentLang());
  }

  get shouldShowCentersEntry(): boolean {
    return this.userCentersCount > 1;
  }

  get currentCenterName(): string {
    if (!this.currentCenter) {
      return '';
    }

    return this.availableCenters.find((center) => center.center === this.currentCenter)?.name || this.currentCenter;
  }

  onMarkAllUserNotificationsRead() {
    this.userNotifications = this.userNotifications.map((n) => ({
      ...n,
      status: 'msg-read',
    }));
  }

  onReadAllUserNotifications() {
    this.router.navigate(['/email/inbox']);
  }

  onRemoveUserNotification(notification: Notifications) {
    this.userNotifications = this.userNotifications.filter((n) => n !== notification);
  }

  onUserNotificationActionClick(event: { notification: Notifications; actionType: string }) {
    const { actionType } = event;

    switch (actionType) {
      case 'view':
      case 'reply':
      case 'download':
      case 'mark-important':
      default:
        this.router.navigate(['/email/inbox']);
    }
  }

  onMarkAllSystemUpdatesRead() {
    this.systemUpdateNotifications = this.systemUpdateNotifications.map((n) => ({
      ...n,
      status: 'msg-read',
    }));
    this.persistSeenSystemUpdates(
      this.systemUpdateNotifications.map((notification) => notification.id).filter(Boolean) as string[]
    );
  }

  onReadAllSystemUpdates() {
    this.router.navigate(['/config/system-updates']);
  }

  onRemoveSystemUpdateNotification(notification: Notifications) {
    if (notification.id) {
      this.persistSeenSystemUpdates([...(this.getSeenSystemUpdates()), notification.id]);
    }
    this.systemUpdateNotifications = this.systemUpdateNotifications.filter((n) => n !== notification);
  }

  onSystemUpdateNotificationRead(notification: Notifications) {
    if (!notification.id) {
      return;
    }
    this.persistSeenSystemUpdates([...(this.getSeenSystemUpdates()), notification.id]);
  }

  onSystemUpdateNotificationActionClick(event: { notification: Notifications; actionType: string }) {
    const { notification, actionType } = event;

    switch (actionType) {
      case 'view':
        if (notification.id) {
          this.persistSeenSystemUpdates([...(this.getSeenSystemUpdates()), notification.id]);
        }
        if (notification.id) {
          void this.router.navigate(['/config/system-updates', notification.id]);
        } else {
          void this.router.navigate(['/config/system-updates']);
        }
        break;
      default:
        void this.router.navigate(['/config/system-updates']);
    }
  }

  callFullscreen() {
    if (!this.isFullScreen) {
      if (this.docElement?.requestFullscreen != null) {
        this.docElement?.requestFullscreen();
      }
    } else {
      document.exitFullscreen();
    }
    this.isFullScreen = !this.isFullScreen;
  }

  async openWebPunch(): Promise<void> {
    if (!this.webPunchEnabled || this.dialog.getDialogById('web-punch-quick-dialog')) {
      return;
    }
    this.isNavbarCollapsed = true;
    this.closeMobilePanels();
    const { WebPunchDialogComponent } = await import('../../attendances/web-punch/web-punch-dialog.component');
    const dialogRef = this.dialog.open(WebPunchDialogComponent, {
      id: 'web-punch-quick-dialog',
      width: 'min(760px, 100vw)',
      maxWidth: '100vw',
      height: '100dvh',
      maxHeight: '100dvh',
      position: { top: '0', right: '0' },
      autoFocus: false,
      restoreFocus: true,
      panelClass: 'web-punch-quick-dialog',
      backdropClass: 'web-punch-quick-backdrop',
    });
    this.subs.sink = dialogRef.afterClosed().subscribe((result?: { accepted: boolean; message: string }) => {
      if (!result?.message) return;
      this.snackBar.open(result.message, undefined, {
        duration: result.accepted ? 4000 : 6000,
        panelClass: [result.accepted ? 'snackbar-success' : 'snackbar-danger'],
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
      });
    });
  }
  setLanguage(text: string, lang: string, flag: string) {
    this.countryName = text;
    this.flagvalue = flag;
    this.langStoreValue = lang;
    this.languageService.setLanguage(lang);
  }
  mobileMenuSidebarOpen(event: Event, className: string) {
    event.preventDefault();
    event.stopPropagation();

    const isMobileViewport = window.innerWidth < 1025;
    const isSidebarOpen = this.document.body.classList.contains(className);

    if (isSidebarOpen) {
      this.closeMobileSidebar(className);
      return;
    }

    if (isMobileViewport) {
      this.renderer.removeClass(this.document.body, 'ls-closed');
      this.renderer.removeClass(this.document.body, 'side-closed');
      this.renderer.removeClass(this.document.body, 'submenu-closed');
      this.renderer.removeClass(this.document.body, 'side-closed-hover');
      this.localStorageService.set('collapsed_menu', 'false');
    }

    this.renderer.addClass(this.document.body, className);
  }

  callSidemenuCollapse() {
    const hasClass = this.document.body.classList.contains('side-closed');
    if (hasClass) {
      this.renderer.removeClass(this.document.body, 'side-closed');
      this.renderer.removeClass(this.document.body, 'submenu-closed');
      this.localStorageService.set('collapsed_menu', 'false');
    } else {
      this.renderer.addClass(this.document.body, 'side-closed');
      this.renderer.addClass(this.document.body, 'submenu-closed');
      this.localStorageService.set('collapsed_menu', 'true');
    }
  }
  logout() {
    this.subs.sink = this.authService.logout().subscribe((res) => {
      if (res.success) {
        this.router.navigate(['/authentication/signin']);
      }
    });
  }

  onLanguageChange(item: { text: string; flag: string; lang: string }) {
    this.languageService.setLanguage(item.lang);
    this.syncLanguageSelection(item.lang);
    this.isMobileLanguageOpen = false;
  }

  onAccountClicked() {
    this.router.navigate(['/config/profile']);
  }

  onInboxClicked() {
    this.router.navigate(['/email/inbox']);
  }

  onModulosClick() {
    this.router.navigate(['/config/modules']);
    console.log('Abrir modulos');
  }

  onCentersClick() {
    this.router.navigate(['/config/centers']);
  }

  async onCenterSelected(center: string) {
    if (!center || center === this.currentCenter || this.isChangingCenter) {
      return;
    }

    this.isChangingCenter = true;
    try {
      const response = await firstValueFrom(this.authService.switchCenter(center));
      const nextCenter = String(response?.center || center);
      this.appConfigService.setActiveCenter(nextCenter);
      this.currentCenter = nextCenter;
      const browserWindow = this.document.defaultView;
      if (browserWindow) {
        browserWindow.location.reload();
      }
    } catch (error) {
      console.error('Error switching active center', error);
      this.isChangingCenter = false;
    }
  }

  onRolesClick() {
    this.router.navigate(['/config/roles']);
    console.log('Abrir modulos');
  }

  onNotificationsSettingsClicked() {
    console.log('Abrir configuracion de notificaciones');
  }

  onSystemUpdatesClick() {
    this.router.navigate(['/config/system-updates']);
  }

  onCreatePermissAccessClicked() {
    this.router.navigate(['/config/create-permiss']);
    console.log('entro por crear permisos')
  }

  usersSettingsClicked() {
    this.router.navigate(['/config/users']);
    console.log('entro por crear permisos')
  }

  toggleMobileSettings(): void {
    this.isMobileNotificationsOpen = false;
    this.isMobileSystemUpdatesOpen = false;
    this.isMobileLanguageOpen = false;
    this.isMobileSettingsOpen = !this.isMobileSettingsOpen;
    if (this.isMobileSettingsOpen) {
      this.isMobileSettingsGroupOpen = true;
    }
  }

  toggleMobileCentersGroup(): void {
    this.isMobileCentersGroupOpen = !this.isMobileCentersGroupOpen;
  }

  toggleMobileLanguage(): void {
    this.isMobileNotificationsOpen = false;
    this.isMobileSystemUpdatesOpen = false;
    this.isMobileSettingsOpen = false;
    this.isMobileLanguageOpen = !this.isMobileLanguageOpen;
  }
  toggleMobileNotifications(): void {
    this.isMobileSystemUpdatesOpen = false;
    this.isMobileSettingsOpen = false;
    this.isMobileLanguageOpen = false;
    this.isMobileNotificationsOpen = !this.isMobileNotificationsOpen;
  }

  toggleMobileSystemUpdates(): void {
    this.isMobileNotificationsOpen = false;
    this.isMobileSettingsOpen = false;
    this.isMobileLanguageOpen = false;
    this.isMobileSystemUpdatesOpen = !this.isMobileSystemUpdatesOpen;
  }

  toggleMobileSettingsGroup(): void {
    this.isMobileSettingsGroupOpen = !this.isMobileSettingsGroupOpen;
  }

  toggleMobileNavbar(): void {
    this.isNavbarCollapsed = !this.isNavbarCollapsed;
    if (this.isNavbarCollapsed) {
      this.closeMobilePanels();
    }
  }

  get unreadUserNotificationsCount(): number {
    return this.userNotifications.filter((notification) => notification.status === 'msg-unread').length;
  }

  get unreadSystemUpdateNotificationsCount(): number {
    return this.systemUpdateNotifications.filter((notification) => notification.status === 'msg-unread').length;
  }
  private syncCurrentUser() {
    const currentUser = this.authService.currentUserValue;
    this.userImg = currentUser.avatar || '';
    this.userName = currentUser.name;
    this.availableCenters = Array.isArray((currentUser as { centers?: unknown[] }).centers)
      ? ((currentUser as { centers?: CenterAccess[] }).centers || []).filter(
          (center): center is CenterAccess => !!center && typeof center.center === 'string'
        )
      : [];
    this.userCentersCount = this.availableCenters.length;
    this.currentCenter = String(currentUser.center || this.appConfigService.getActiveCenter() || '');
    this.userInitials = this.buildInitials(
      this.userName || currentUser.email || ''
    );
    void this.loadWebPunchAvailability();
  }

  private async loadWebPunchAvailability(): Promise<void> {
    this.webPunchEnabled = false;
    try {
      const bootstrap = await firstValueFrom(this.apiService.get<WebPunchAvailabilityBootstrap>('users/me/bootstrap'));
      const allowedSources = bootstrap.workConfig?.punchPolicy?.allowedSources;
      const hasActiveEmployee = !!bootstrap.employee?.employeeId && bootstrap.employee.isActive !== false;
      const canPunchFromWeb = Array.isArray(allowedSources) && allowedSources.includes('web');
      this.webPunchEnabled = hasActiveEmployee && canPunchFromWeb;
    } catch {
      this.webPunchEnabled = false;
    }
  }

  private buildInitials(value: string): string {
    return value
      .split(/[\s._-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || '')
      .join('') || 'US';
  }

  private syncLanguageSelection(lang: string): void {
    this.langStoreValue = lang || this.localStorageService.get('lang') as string;
    const selected = this.listLang.find((item) => item.lang === this.langStoreValue);
    this.countryName = selected?.text || '';
    this.flagvalue = selected?.flag;
  }

  private buildUserNotifications(): void {
    this.userNotifications = [
      {
        message: this.translate.instant('notifications.items.checkMail.message'),
        time: this.translate.instant('notifications.items.checkMail.time'),
        icon: 'mail',
        color: 'notification-green',
        status: 'msg-unread',
        actionLabel: this.translate.instant('notifications.actions.view'),
        actionType: 'view',
      },
      {
        message: this.translate.instant('notifications.items.newPatient.message'),
        time: this.translate.instant('notifications.items.newPatient.time'),
        userImg: 'assets/images/user/user1.jpg',
        color: 'notification-blue',
        status: 'msg-unread',
      },
      {
        message: this.translate.instant('notifications.items.leaveApproved.message'),
        time: this.translate.instant('notifications.items.leaveApproved.time'),
        icon: 'event_available',
        color: 'notification-orange',
        status: 'msg-read',
      },
      {
        message: this.translate.instant('notifications.items.lunch.message'),
        time: this.translate.instant('notifications.items.lunch.time'),
        userImg: 'assets/images/user/user2.jpg',
        color: 'notification-blue',
        status: 'msg-unread',
        actionLabel: this.translate.instant('notifications.actions.reply'),
        actionType: 'reply',
      },
    ];
  }

  private async loadSystemUpdates(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.apiService.get<SystemUpdateListResponse>('system-updates', {
          params: {
            isActive: 'true',
            page: 1,
            perPage: 6,
          },
        })
      );
      this.systemUpdateNotifications = (response?.data || []).map((update) => this.mapSystemUpdateToNotification(update));
    } catch (error) {
      console.error('Error loading system updates for header', error);
      this.systemUpdateNotifications = [];
    }
  }

  private mapSystemUpdateToNotification(update: SystemUpdateConfig): Notifications {
    const seenIds = this.getSeenSystemUpdates();
    return {
      id: update.systemUpdateId,
      title: update.title || this.translate.instant('systemUpdatesCatalog.labels.untitled'),
      message: update.description || this.translate.instant('systemUpdatesCatalog.labels.noDescription'),
      time: this.buildSystemUpdateMeta(update),
      icon: update.source === 'bitbucket' ? 'fork_right' : 'system_update_alt',
      color: update.source === 'bitbucket' ? 'notification-blue' : 'notification-green',
      status: seenIds.includes(update.systemUpdateId) ? 'msg-read' : 'msg-unread',
      actionLabel: this.translate.instant('notifications.actions.view'),
      actionType: 'view',
    };
  }

  private buildSystemUpdateMeta(update: SystemUpdateConfig): string {
    const parts: string[] = [];

    if (update.version) {
      parts.push(`${this.translate.instant('systemUpdatesCatalog.labels.versionShort')} ${update.version}`);
    }

    parts.push(
      update.source === 'bitbucket'
        ? this.translate.instant('systemUpdatesCatalog.sourceOptions.bitbucket')
        : this.translate.instant('systemUpdatesCatalog.sourceOptions.manual')
    );

    if (update.commitDate) {
      parts.push(this.formatDateLabel(update.commitDate));
    }

    return parts.join(' · ');
  }

  private formatDateLabel(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat(this.translate.currentLang || 'es', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(date);
  }

  private getSeenSystemUpdates(): string[] {
    const stored = this.localStorageService.get(this.seenSystemUpdatesStorageKey);
    if (typeof stored !== 'string' || !stored.trim()) {
      return [];
    }

    try {
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string') : [];
    } catch {
      return [];
    }
  }

  private persistSeenSystemUpdates(ids: string[]): void {
    this.localStorageService.set(this.seenSystemUpdatesStorageKey, JSON.stringify(Array.from(new Set(ids))));
  }

  private closeMobilePanels(): void {
    this.isMobileSettingsOpen = false;
    this.isMobileLanguageOpen = false;
    this.isMobileNotificationsOpen = false;
    this.isMobileSystemUpdatesOpen = false;
    this.isMobileSettingsGroupOpen = true;
    this.isMobileCentersGroupOpen = false;
  }

  private closeMobileSidebar(className: string): void {
    this.renderer.removeClass(this.document.body, className);

    if (window.innerWidth < 1025) {
      this.renderer.addClass(this.document.body, 'ls-closed');
      this.renderer.addClass(this.document.body, 'side-closed');
      this.renderer.addClass(this.document.body, 'submenu-closed');
      this.renderer.removeClass(this.document.body, 'side-closed-hover');
      this.localStorageService.set('collapsed_menu', 'true');
    }
  }
}
